import type { FC } from 'react';
import { createElement, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getDevEnv } from 'service/envConfig';
import { getToken } from '@dvag/dfs-orchestrator-client';
import { getEnvironmentAppUrls } from '@dvag/dfs-constant-config';
import { createRegisteredChildMfe } from 'mfeBridge/createRegisteredChildMfe';

type KundenstammdatenEventOptions = {
  kundennummer?: string;
  haushaltId?: string;
  isDirty?: boolean;
};

type Props = {
  onNavigateBack: () => void;
  onNavigateToKss: (kundennummer: string) => void;
  onDirtyStateChange: (isDirty: boolean) => void;
  onHouseholdCreated: (haushaltId: string) => void;
  getContinuationHandler: (next: () => void) => () => void;
};

type BridgeHandlers = Pick<
  Props,
  | 'onNavigateBack'
  | 'onNavigateToKss'
  | 'onDirtyStateChange'
  | 'onHouseholdCreated'
  | 'getContinuationHandler'
>;

const mfeBridgeIdentifier = 'kundenstammdaten';
const kundenstammdatenBridge = createRegisteredChildMfe<
  BridgeHandlers,
  KundenstammdatenEventOptions
>(mfeBridgeIdentifier);

export const KundenstammdatenSchnellerfassungWebComponent: FC<Props> = ({
  onNavigateBack,
  onNavigateToKss,
  onDirtyStateChange,
  onHouseholdCreated,
  getContinuationHandler,
}) => {
  const { householdId } = useParams<{ householdId: string }>();

  useEffect(
    () =>
      kundenstammdatenBridge.bindHandlers({
        onNavigateBack,
        onNavigateToKss,
        onDirtyStateChange,
        onHouseholdCreated,
        getContinuationHandler,
      }),
    [
      getContinuationHandler,
      onDirtyStateChange,
      onHouseholdCreated,
      onNavigateBack,
      onNavigateToKss,
    ],
  );

  useEffect(() => {
    kundenstammdatenBridge.register((eventName, options, handlers) => {
      if (!handlers) {
        return;
      }

      const opts = options ?? {};

      if (eventName === 'dirtyState') {
        handlers.onDirtyStateChange(!!opts.isDirty);
        return;
      }

      if (eventName === 'navigateBack') {
        handlers.getContinuationHandler(handlers.onNavigateBack)();
        return;
      }

      if (opts.kundennummer && eventName === 'navigateToKss') {
        handlers.getContinuationHandler(() =>
          handlers.onNavigateToKss(opts.kundennummer as string),
        )();
        return;
      }

      if (opts.haushaltId && eventName === 'household-created') {
        handlers.getContinuationHandler(() =>
          handlers.onHouseholdCreated(opts.haushaltId as string),
        )();
      }
    });
  }, []);

  useEffect(() => {
    const env = getDevEnv();
    const appUrls = getEnvironmentAppUrls(env);
    const scriptSrc = appUrls.kundenstammdaten + '/schnellerfassung/schnellerfassung-frontend.js';

    type KundenstammdatenElement = HTMLElement & {
      authenticationToken?: () => Promise<string | void>;
      haushaltId?: string;
      features?: string;
    };

    const getElement = (): KundenstammdatenElement | null =>
      document.querySelector('dvag-kundenstammdaten-schnellerfassung');

    const setLuigiAuthenticationProvider = (): void => {
      const element = getElement();
      if (!element) {
        return;
      }

      element.authenticationToken = (): Promise<string | void> => Promise.resolve(getToken());

      if (householdId) {
        element.haushaltId = householdId;
      }
    };

    if (!document.querySelector(`script[src="${scriptSrc}"]`)) {
      const script = document.createElement('script');
      script.src = scriptSrc;
      script.type = 'module';
      script.addEventListener('load', setLuigiAuthenticationProvider);
      document.head.appendChild(script);
    }

    setLuigiAuthenticationProvider();
  }, [householdId]);

  return createElement('dvag-kundenstammdaten-schnellerfassung', {
    'mfe-bridge-identifier': mfeBridgeIdentifier,
  });
};
